import { Box, InputAdornment, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Cafe, OrderItem } from '../../apiTypes';
import Form from '../../components/forms/Form';
import FormRatio from '../../components/forms/FormRatio';
import FormSelect from '../../components/forms/FormSelect';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import FormUploadImg from '../../components/FormUploadImg';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { updateDoc } from '../../utils/api';
import useMedia from '../../utils/useMedia';
import Bill from '../POS/Bill';

const bottomMarginOptions = ['0', '5', '10', '15', '20', '25', '30'];

const BillSetting = () => {
  const { cafe, cafeSlug } = useAdmin();
  const { dishes, dishesMap } = useCafe();
  const { lang } = useLocale();
  const { isMobile } = useMedia();

  const form = useForm<NonNullable<Cafe['bill']>>({
    defaultValues: {
      width: cafe?.bill?.width || '60',
      logo: cafe?.bill?.logo || '',
      title: cafe?.bill?.title || '',
      beforeText: cafe?.bill?.beforeText || '',
      beforeText2: cafe?.bill?.beforeText2 || '',
      afterText: cafe?.bill?.afterText || '',
      afterText2: cafe?.bill?.afterText2 || '',
      boldText: cafe?.bill?.boldText || false,
      defaultService: cafe?.bill?.defaultService || 0,
      bottomMargin: cafe?.bill?.bottomMargin || '0',
    },
  });
  const bill = form.watch();

  const items = useMemo(() => {
    const items: Record<string, OrderItem> = {};

    if (dishes[0]) {
      items.a = {
        id: 'a',
        dishId: dishes[0].id,
        status: 'complete',
        queue: '1',
        addTime: 1,
        legalEntityId: null,
        cost: null,
      };
    }

    if (dishes[1]) {
      items.b = {
        id: 'b',
        dishId: dishes[1].id,
        status: 'complete',
        queue: '1',
        addTime: 1,
        legalEntityId: null,
        cost: null,
      };
      items.c = {
        id: 'c',
        dishId: dishes[1].id,
        status: 'complete',
        queue: '1',
        addTime: 1,
        legalEntityId: null,
        cost: null,
      };
    }

    return items;
  }, [dishes]);

  const submit = useCallback(
    async (values: NonNullable<Cafe['bill']>) => {
      await updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        bill: values,
      });
      form.reset(values);
    },
    [cafeSlug, form]
  );

  return (
    <Form form={form} onSubmit={submit}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <PrimaryButton variant="contained" type="submit" disabled={!form.formState.isDirty}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 5, justifyContent: 'space-between', pb: 5 }}>
        <div>
          <Typography variant="h6">{t.paper_width[lang]}</Typography>
          <FormRatio
            name="width"
            options={[
              { value: '80', label: `80 ${t.mm[lang]}` },
              { value: '60', label: `60 ${t.mm[lang]}` },
            ]}
          />

          <Typography variant="h6" sx={{ mt: 3 }}>
            {t.restaurant_logo[lang]}
          </Typography>
          <FormUploadImg name="logo" folder="bill_logo" buttonTitle={t.restaurant_logo[lang]} />

          <FormTextField
            name="title"
            label={t.title[lang]}
            fullWidth
            size="small"
            sx={{ mt: 5, div: { backgroundColor: (t) => t.palette.background.paper } }}
          />

          <FormTextField
            name="beforeText"
            multiline
            fullWidth
            size="small"
            label={t.initial_text[lang]}
            sx={{ mt: 2, div: { backgroundColor: (t) => t.palette.background.paper } }}
          />

          <FormTextField
            name="beforeText2"
            multiline
            fullWidth
            size="small"
            label={`${t.initial_text[lang]} 2`}
            sx={{ mt: 2, div: { backgroundColor: (t) => t.palette.background.paper } }}
          />

          <FormTextField
            name="afterText"
            multiline
            fullWidth
            label={t.final_text[lang]}
            size="small"
            sx={{ mt: 2, div: { backgroundColor: (t) => t.palette.background.paper } }}
          />

          <FormTextField
            name="afterText2"
            multiline
            fullWidth
            label={`${t.final_text[lang]} 2`}
            size="small"
            sx={{ mt: 2, div: { backgroundColor: (t) => t.palette.background.paper } }}
          />

          <Box sx={{ mt: 2 }}>
            <FormSwitch name="boldText" color="primary" label={t.bold_font[lang]} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormSelect name="bottomMargin" label={t.bottom_margin[lang]} options={bottomMarginOptions} size="small" />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormTextField
              name="defaultService"
              label={t.service_charge[lang]}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (typeof val === 'number' && val >= 0 && val < 100) {
                  form.setValue('defaultService', val, { shouldDirty: true });
                }
              }}
              sx={{ mt: 1 }}
            />
          </Box>
        </div>

        {!isMobile && cafe && (
          <Box sx={{ width: bill?.width === '80' ? 400 : 300, bgcolor: (t) => t.palette.grey[100], flexShrink: 0 }}>
            <Bill
              cafe={{ ...cafe, bill }}
              dishesMap={dishesMap}
              service={bill.defaultService || 0}
              discount={10}
              items={items}
              number={1000}
              created={Date.now()}
              promotionsApplied={[]}
              legalEntities={[]}
              legalEntitiesMap={{}}
            />
          </Box>
        )}
      </Box>
    </Form>
  );
};

export default BillSetting;
